"use client";

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
  useRef,
  useSyncExternalStore,
} from "react";
import { useAccountSelection } from "./account-selection-context";
import { useAccountEventStream } from "@/hooks/use-account-event-stream";
import { useMarketData, type MarketDataSymbol } from "@/hooks/use-market-data";
import { useMarketDataStreams } from "@/hooks/use-market-data-streams";
import { apiClient } from "@/lib/api-client";
import { toChannelKey } from "@/lib/symbols/symbol-identity";
import { symbolMappingService } from "@/services/symbol-mapping-service";
import { positionMetricsService } from "@/services/position-metrics-service";
import { calculateDTE, parseOptionSymbol } from "@/utils/option-symbol-utils";
import {
  positionEventBus,
  type PositionEvent,
  type MarketDataEvent,
  type GreeksEvent,
} from "@/lib/events/position-events";
import type { EnhancedPosition, Position } from "@/types/position.types";
import { createLogger } from "@/utils/logger";
import { cacheAggregator } from "@/services/unified-cache-aggregator";
import { extractPositionsFromResponse, isPlainObject } from "@/hooks/types";
import type { StreamerMessage } from "@/services/account-streamer";
import { toStreamerMessageFromAccountEvent } from "./unified-positions.helpers";

const logger = createLogger('unified-positions-context');

const canonicalizeSymbol = (symbol: string | null | undefined): string => {
  return symbol ? toChannelKey(symbol) : "";
};

const aliasMapsEqual = (
  a: Map<string, Set<string>>,
  b: Map<string, Set<string>>,
): boolean => {
  if (a.size !== b.size) {
    return false;
  }

  for (const [key, setA] of a.entries()) {
    const setB = b.get(key);
    if (!setB || setA.size !== setB.size) {
      return false;
    }
    for (const value of setA.values()) {
      if (!setB.has(value)) {
        return false;
      }
    }
  }

  return true;
};

const cloneAliasMap = (source: Map<string, Set<string>>): Map<string, Set<string>> => {
  const clone = new Map<string, Set<string>>();
  source.forEach((set, key) => {
    clone.set(key, new Set(set));
  });
  return clone;
};

const serializeMarketDataSymbol = (symbol: MarketDataSymbol): string => {
  const base = canonicalizeSymbol(symbol.symbol);
  const stream = canonicalizeSymbol(symbol.streamerSymbol);
  const underlying = canonicalizeSymbol(symbol.underlyingSymbol);
  return `${symbol.instrumentType}|${stream}|${base}|${underlying}`;
};

const areSymbolListsEqual = (
  current: MarketDataSymbol[] | undefined,
  next: MarketDataSymbol[],
): boolean => {
  if (!current) {
    return next.length === 0;
  }
  if (current.length !== next.length) {
    return false;
  }

  const currentTokens = current.map(serializeMarketDataSymbol).sort();
  const nextTokens = next.map(serializeMarketDataSymbol).sort();

  for (let index = 0; index < currentTokens.length; index += 1) {
    if (currentTokens[index] !== nextTokens[index]) {
      return false;
    }
  }

  return true;
};

const aggregateExternalSymbols = (
  sources: Map<string, MarketDataSymbol[]>,
): Map<string, MarketDataSymbol> => {
  const merged = new Map<string, MarketDataSymbol>();

  sources.forEach((symbols) => {
    symbols.forEach((entry) => {
      if (!entry) {
        return;
      }
      const token = serializeMarketDataSymbol(entry);
      if (!merged.has(token)) {
        merged.set(token, entry);
      }
    });
  });

  return merged;
};

const normalizeDateValue = (value: unknown): Date | null => {
  if (!value) {
    return null;
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value;
  }

  if (typeof value === "number" && Number.isFinite(value)) {
    const fromNumber = new Date(value);
    return Number.isNaN(fromNumber.getTime()) ? null : fromNumber;
  }

  if (typeof value === "string") {
    if (!value.trim()) {
      return null;
    }

    const parsed = new Date(value);
    if (!Number.isNaN(parsed.getTime())) {
      return parsed;
    }

    if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
      const [year, month, day] = value.split("-").map(Number);
      const fromParts = new Date(year, (month ?? 1) - 1, day ?? 1);
      return Number.isNaN(fromParts.getTime()) ? null : fromParts;
    }
  }

  return null;
};

const isSameCalendarDay = (first: Date | null, second: Date | null): boolean => {
  if (!first || !second) {
    return false;
  }

  const a = new Date(first);
  const b = new Date(second);

  a.setHours(0, 0, 0, 0);
  b.setHours(0, 0, 0, 0);

  return a.getTime() === b.getTime();
};

const getExpirationDate = (symbol: string): Date | null => {
  const parsed = parseOptionSymbol(symbol);
  if (!parsed) {
    return null;
  }

  const year = 2000 + parseInt(parsed.expiry.slice(0, 2), 10);
  const month = parseInt(parsed.expiry.slice(2, 4), 10) - 1;
  const day = parseInt(parsed.expiry.slice(4, 6), 10);

  const expiry = new Date(year, month, day);
  return Number.isNaN(expiry.getTime()) ? null : expiry;
};

const getPositionOpenDate = (position: UnifiedPosition): Date | null => {
  const record = position as UnifiedPosition &
    Record<string, unknown> &
    { openedAt?: unknown };

  const candidate =
    record["trade-date"] ??
    record.openedAt ??
    record["opened-at"] ??
    record["created-at"] ??
    null;

  return normalizeDateValue(candidate);
};

const loadInitialGreeksData = (): Map<string, Greeks> => {
  if (typeof window === 'undefined') {
    return new Map();
  }

  const cached = window.localStorage.getItem('cachedGreeksData');
  if (!cached) {
    return new Map();
  }

  try {
    const parsed = JSON.parse(cached) as Record<string, Greeks>;
    return new Map(Object.entries(parsed));
  } catch (error) {
    logger.error('Failed to parse cached Greeks:', error);
    return new Map();
  }
};

interface MarketQuote {
  bidPrice?: number;
  askPrice?: number;
  lastPrice?: number;
  midPrice?: number;
  /** Prior-session close. Not populated by the streamer today — consumers
   *  must treat absence as "day change unknown", never default it to 0. */
  prevClosePrice?: number;
  volume?: number;
  timestamp?: number; // ms since epoch
}

interface Greeks {
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  impliedVolatility?: number;
  timestamp?: number; // ms since epoch
}

export interface PositionStrategyMetadata {
  orderId: string;
  accountNumber: string;
  strategyId: string | null;
  strategyName: string | null;
  strategyType: string | null;
  strategySource: string | null;
  symbol: string;
  underlyingSymbol: string | null;
  status: string | null;
  targetQuantity: number | null;
  profitTarget: number | null;
  stopLoss: number | null;
  trailingStop: Record<string, unknown> | null;
  positionSymbols: string[];
}

interface StrategyOrderSummary {
  orderId: string;
  accountNumber: string;
  strategyId?: string | null;
  strategyName?: string | null;
  strategyType?: string | null;
  strategySource?: string | null;
  symbol: string;
  underlyingSymbol?: string | null;
  status?: string | null;
  targetQuantity?: number | null;
  profitTarget?: number | null;
  stopLoss?: number | null;
  trailingStop?: Record<string, unknown> | null;
  positionSymbols?: string[] | null;
}

type PositionGreeksExtras = {
  delta?: number;
  gamma?: number;
  theta?: number;
  vega?: number;
  impliedVolatility?: number;
  "streamer-symbol"?: string;
};

type UnifiedPosition = Position &
  PositionGreeksExtras & {
    strategyMetadata?: PositionStrategyMetadata;
    lastUpdate?: Date;
  };

const toNumeric = (value: unknown): number | null => {
  if (typeof value === "number" && Number.isFinite(value)) {
    return value;
  }
  if (typeof value === "string") {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
  }
  return null;
};

interface NormalizedStreamerPositionUpdate {
  symbol: string;
  accountNumber: string;
  payload: Partial<UnifiedPosition> & Record<string, unknown>;
  action: string | null;
  quantity: number | null;
  rawType: string;
}

const normalizeStreamerPositionUpdate = (
  message: StreamerMessage | null | undefined,
): NormalizedStreamerPositionUpdate | null => {
  if (!message || !message.data || typeof message.data !== "object") {
    return null;
  }

  const root = message.data as Record<string, unknown>;
  const payload = isPlainObject(root.data)
    ? (root.data as Record<string, unknown>)
    : root;

  const symbolCandidate =
    typeof payload.symbol === "string"
      ? payload.symbol
      : typeof root.symbol === "string"
        ? root.symbol
        : undefined;

  const accountCandidate =
    typeof payload["account-number"] === "string"
      ? payload["account-number"]
      : typeof root["account-number"] === "string"
        ? root["account-number"]
        : undefined;

  const symbol = symbolCandidate?.trim();
  const accountNumber = accountCandidate?.toString().trim();

  if (!symbol || !accountNumber) {
    return null;
  }

  const actionCandidate =
    typeof payload.action === "string"
      ? payload.action
      : typeof root.action === "string"
        ? root.action
        : typeof payload.type === "string"
          ? payload.type
          : typeof root.type === "string"
            ? root.type
            : message.type;

  const quantityValue = toNumeric(
    (payload as { quantity?: unknown }).quantity ??
      (payload as { "leaves-quantity"?: unknown })["leaves-quantity"],
  );

  return {
    symbol,
    accountNumber,
    payload: payload as Partial<UnifiedPosition> & Record<string, unknown>,
    action: actionCandidate ?? null,
    quantity: quantityValue,
    rawType: message.type,
  };
};

const normalizePositionSymbol = (symbol: string | null | undefined) =>
  (symbol || "")
    .replace(/\s+/g, " ")
    .trim()
    .toUpperCase();

function strategyMetadataEquals(
  a: PositionStrategyMetadata,
  b: PositionStrategyMetadata,
): boolean {
  const trailingEqual =
    JSON.stringify(a.trailingStop ?? null) ===
    JSON.stringify(b.trailingStop ?? null);

  if (!trailingEqual) return false;

  if (a.positionSymbols.length !== b.positionSymbols.length) return false;
  for (let i = 0; i < a.positionSymbols.length; i++) {
    if (a.positionSymbols[i] !== b.positionSymbols[i]) {
      return false;
    }
  }

  return (
    a.orderId === b.orderId &&
    a.accountNumber === b.accountNumber &&
    a.strategyId === b.strategyId &&
    a.strategyName === b.strategyName &&
    a.strategyType === b.strategyType &&
    a.strategySource === b.strategySource &&
    a.symbol === b.symbol &&
    a.underlyingSymbol === b.underlyingSymbol &&
    a.status === b.status &&
    a.targetQuantity === b.targetQuantity &&
    a.profitTarget === b.profitTarget &&
    a.stopLoss === b.stopLoss
  );
}

const DEFAULT_STRATEGY_GROUP_WINDOW_MS = 30_000;

function summarizePositionsForComparison(positions: UnifiedPosition[]) {
  return positions.map((p) => ({
    symbol: p.symbol,
    quantity: p.quantity,
    'account-number': p['account-number'],
    'average-open-price': p['average-open-price'],
  }));
}

function groupPositionsByHeuristic(
  positions: UnifiedPosition[],
  timeThreshold = DEFAULT_STRATEGY_GROUP_WINDOW_MS,
): UnifiedPosition[][] {
  if (!positions || positions.length === 0) {
    return [];
  }

  const strategyGroups: UnifiedPosition[][] = [];
  const sortedPositions = [...positions].sort((a, b) => {
    const timeA = new Date(a["created-at"] || 0).getTime();
    const timeB = new Date(b["created-at"] || 0).getTime();
    return timeA - timeB;
  });

  sortedPositions.forEach((position) => {
    const posTime = new Date(position["created-at"] || 0).getTime();
    const underlying = position["underlying-symbol"];

    let foundGroup = false;
    for (const group of strategyGroups) {
      if (group.length === 0) continue;
      const groupUnderlying = group[0]["underlying-symbol"];
      const groupTime = new Date(group[0]["created-at"] || 0).getTime();

      if (
        underlying === groupUnderlying &&
        Math.abs(posTime - groupTime) <= timeThreshold
      ) {
        group.push(position);
        foundGroup = true;
        break;
      }
    }

    if (!foundGroup) {
      strategyGroups.push([position]);
    }
  });

  return strategyGroups;
}

function computeStrategyGroupCount(
  positions: UnifiedPosition[],
  strategyMap: Map<string, PositionStrategyMetadata>,
  timeThreshold = DEFAULT_STRATEGY_GROUP_WINDOW_MS,
) {
  if (!positions || positions.length === 0) {
    return {
      count: 0,
      groups: [] as Array<{
        source: string;
        key: string;
        underlying?: string | null;
        legs: number;
        positions: string[];
        orderId?: string | null;
        accountNumber?: string | null;
      }>,
    };
  }

  const metadataGroups = new Map<
    string,
    { meta: PositionStrategyMetadata; positions: UnifiedPosition[] }
  >();
  const uncovered: UnifiedPosition[] = [];

  positions.forEach((position) => {
    const normalizedSymbol = normalizePositionSymbol(position.symbol);
    const positionMeta =
      (position.strategyMetadata as PositionStrategyMetadata | undefined) ||
      strategyMap.get(normalizedSymbol);

    if (positionMeta && positionMeta.orderId) {
      const accountKey =
        positionMeta.accountNumber || position["account-number"] || "UNKNOWN";
      const key = `${accountKey}:${positionMeta.orderId}`;
      if (!metadataGroups.has(key)) {
        metadataGroups.set(key, { meta: positionMeta, positions: [] });
      }
      metadataGroups.get(key)!.positions.push(position);
    } else {
      uncovered.push(position);
    }
  });

  const heuristicGroups = groupPositionsByHeuristic(uncovered, timeThreshold);

  const debugGroups: Array<{
    source: string;
    key: string;
    underlying?: string | null;
    legs: number;
    positions: string[];
    orderId?: string | null;
    accountNumber?: string | null;
    strategyName?: string | null;
  }> = [];

  metadataGroups.forEach(({ meta, positions }, key) => {
    debugGroups.push({
      source: "metadata",
      key,
      underlying: meta.underlyingSymbol,
      legs: meta.positionSymbols?.length || positions.length || 1,
      positions: positions.map((p) => p.symbol),
      orderId: meta.orderId,
      accountNumber: meta.accountNumber,
      strategyName: meta.strategyName,
    });
  });

  heuristicGroups.forEach((group, index) => {
    debugGroups.push({
      source: "heuristic",
      key: `heuristic-${index}`,
      underlying: group[0]?.["underlying-symbol"],
      legs: group.length,
      positions: group.map((p) => p.symbol),
    });
  });

  return {
    count: metadataGroups.size + heuristicGroups.length,
    groups: debugGroups,
  };
}

interface UnifiedPositionsContextType {
  // Core position data
  positions: UnifiedPosition[];
  enhancedPositions: EnhancedPosition[];
  
  // Filtered views (auto-computed)
  weeklyPositions: UnifiedPosition[];      // 0-5 DTE
  zeroDtePositions: UnifiedPosition[];     // 0 DTE exactly
  weeklyGroupedCount: number;  // Count of grouped weekly positions
  zeroDteGroupedCount: number; // Count of grouped 0DTE positions
  
  // Real-time market data
  marketData: Map<string, MarketQuote>;
  greeksData: Map<string, Greeks>;
  positionStrategies: Map<string, PositionStrategyMetadata>;
  
  // Connection status
  isConnected: boolean;
  accountStreamerConnected: boolean;
  lastUpdate: Date | null;
  
  // Update methods
  refreshPositions: () => Promise<void>;
  cleanupClosedPositions: () => void;
  
  // Loading states
  loading: boolean;
  error: string | null;
  
  // For external monitoring systems
  getPositionsSnapshot: () => UnifiedPosition[];
  getMarketDataSnapshot: () => Map<string, MarketQuote>;

  // External symbol requests (e.g., review orders)
  addExternalSymbols: (sourceKey: string, symbols: MarketDataSymbol[]) => void;
  clearExternalSymbols: (sourceKey?: string) => void;
}

interface UnifiedPositionsContextInternal {
  base: Omit<UnifiedPositionsContextType, "marketData" | "greeksData">;
  marketDataStore: {
    subscribe: (listener: () => void) => () => void;
    getSnapshot: () => Map<string, MarketQuote>;
  };
  greeksDataStore: {
    subscribe: (listener: () => void) => () => void;
    getSnapshot: () => Map<string, Greeks>;
  };
}

const UnifiedPositionsContext = createContext<UnifiedPositionsContextInternal | undefined>(undefined);

export function useUnifiedPositions() {
  const context = useContext(UnifiedPositionsContext);
  if (!context) {
    throw new Error("useUnifiedPositions must be used within a UnifiedPositionsProvider");
  }
  const marketData = useSyncExternalStore(
    context.marketDataStore.subscribe,
    context.marketDataStore.getSnapshot,
    context.marketDataStore.getSnapshot,
  );

  const greeksData = useSyncExternalStore(
    context.greeksDataStore.subscribe,
    context.greeksDataStore.getSnapshot,
    context.greeksDataStore.getSnapshot,
  );

  return useMemo<UnifiedPositionsContextType>(() => ({
    ...context.base,
    marketData,
    greeksData,
  }), [context.base, marketData, greeksData]);
}

interface UnifiedPositionsProviderProps {
  children: ReactNode;
}

export function UnifiedPositionsProvider({ children }: UnifiedPositionsProviderProps) {
  const { selectedAccounts } = useAccountSelection();
  const accountNumbers = useMemo(
    () =>
      selectedAccounts
        .map((account) => account.account["account-number"]?.trim())
        .filter((value): value is string => !!value && value.length > 0),
    [selectedAccounts],
  );
  
  // Core state
  const [positions, setPositions] = useState<UnifiedPosition[]>([]);
  const [enhancedPositions, setEnhancedPositions] = useState<EnhancedPosition[]>([]);
  const marketDataRef = useRef<Map<string, MarketQuote>>(new Map());
  const marketDataListeners = useRef<Set<() => void>>(new Set());
  const greeksDataRef = useRef<Map<string, Greeks>>(loadInitialGreeksData());
  const greeksDataListeners = useRef<Set<() => void>>(new Set());
  const positionEventTimestampsRef = useRef<Record<string, number>>({});
  const lastStreamActivityRef = useRef<number>(0);
  const [lastUpdate, setLastUpdate] = useState<Date | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [positionStrategies, setPositionStrategies] = useState<
    Map<string, PositionStrategyMetadata>
  >(new Map());

  // Market data hook for real-time updates
  const [subscriptionAliases, setSubscriptionAliases] = useState<
    Map<string, Set<string>>
  >(new Map());

  const activeSymbols = useMemo(
    () => Array.from(subscriptionAliases.keys()),
    [subscriptionAliases],
  );

  const {
    quotes,
    greeks,
    isConnected: legacyMarketConnected,
    subscribe: subscribeToMarketData,
  } = useMarketData();

  const {
    ticks,
    greeks: streamedGreeks,
    status: streamStatus,
  } = useMarketDataStreams(activeSymbols, { includeGreeks: true });

  const marketDataConnected = useMemo(() => {
    if (activeSymbols.length === 0) {
      return legacyMarketConnected;
    }
    const sseConnected = activeSymbols.some(
      (symbol) => streamStatus[symbol] === "connected",
    );
    return sseConnected || legacyMarketConnected;
  }, [activeSymbols, streamStatus, legacyMarketConnected]);

  const { positions: accountPositionEvents, status: accountEventStatus } =
    useAccountEventStream(accountNumbers, { includePositions: true });

  const accountStreamerConnected = useMemo(() => {
    if (accountNumbers.length === 0) {
      return Object.values(accountEventStatus).some(
        (value) => value === "connected",
      );
    }
    return accountNumbers.some(
      (accountNumber) => accountEventStatus[accountNumber] === "connected",
    );
  }, [accountNumbers, accountEventStatus]);

  // External market data symbol requests (e.g., review-order strikes)
  const [externalSymbolSources, setExternalSymbolSources] = useState<
    Map<string, MarketDataSymbol[]>
  >(new Map());

  const aggregatedExternalSymbols = useMemo(
    () => aggregateExternalSymbols(externalSymbolSources),
    [externalSymbolSources],
  );

  // Stable helpers to manage external symbols without causing update loops
  const addExternalSymbols = useCallback(
    (sourceKey: string, symbols: MarketDataSymbol[]) => {
      const sanitized = symbols
        .filter(Boolean)
        .map((entry) => ({ ...entry }));

      setExternalSymbolSources((prev) => {
        const existing = prev.get(sourceKey);
        if (areSymbolListsEqual(existing, sanitized)) {
          return prev;
        }

        const next = new Map(prev);
        if (sanitized.length > 0) {
          next.set(sourceKey, sanitized);
        } else {
          next.delete(sourceKey);
        }
        return next;
      });
    },
    [],
  );

  const clearExternalSymbols = useCallback((sourceKey?: string) => {
    setExternalSymbolSources((prev) => {
      if (sourceKey === undefined) {
        if (prev.size === 0) {
          return prev;
        }
        return new Map();
      }

      if (!prev.has(sourceKey)) {
        return prev;
      }

      const next = new Map(prev);
      next.delete(sourceKey);
      return next;
    });
  }, []);

  // Memoized filtered views
  const zeroDtePositions = useMemo(() => {
    const today = new Date();

    return positions.filter((position) => {
      const dte = calculateDTE(position.symbol);
      if (dte !== 0) {
        return false;
      }

      const openedAt = getPositionOpenDate(position);
      return isSameCalendarDay(openedAt, today);
    });
  }, [positions]);

  const zeroDteKeys = useMemo(() => {
    const keys = new Set<string>();

    zeroDtePositions.forEach((position) => {
      const account = position["account-number"] ?? "UNKNOWN";
      keys.add(`${account}:${position.symbol}`);
    });

    return keys;
  }, [zeroDtePositions]);

  const weeklyPositions = useMemo(() => {
    const today = new Date();
    const todayDow = today.getDay();
    const maxWeeklyDte = todayDow === 6 ? 6 : 5; // Allow Saturday preview of next Friday expiry

    return positions
      .filter((position) => {
        const account = position["account-number"] ?? "UNKNOWN";
        if (zeroDteKeys.has(`${account}:${position.symbol}`)) {
          return false;
        }

        const dte = calculateDTE(position.symbol);
        if (dte === null || dte < 0 || dte > maxWeeklyDte) {
          return false;
        }

        const expiry = getExpirationDate(position.symbol);
        if (!expiry || expiry.getDay() !== 5) {
          return false;
        }

        if (dte === 0) {
          const openedAt = getPositionOpenDate(position);
          if (isSameCalendarDay(openedAt, today)) {
            return false;
          }
        }

        return true;
      })
      .sort((a, b) => {
        const dteA = calculateDTE(a.symbol) || 999;
        const dteB = calculateDTE(b.symbol) || 999;
        if (dteA !== dteB) return dteA - dteB;

        const plA = parseFloat(a["day-change-percent"]?.toString() || "0");
        const plB = parseFloat(b["day-change-percent"]?.toString() || "0");
        return plB - plA;
      });
  }, [positions, zeroDteKeys]);

  // Calculate grouped counts (by order-id)
  const weeklyGroupedCount = useMemo(() => {
    const { count } = computeStrategyGroupCount(
      weeklyPositions,
      positionStrategies,
    );
    return count;
  }, [weeklyPositions, positionStrategies]);

  const zeroDteGroupedCount = useMemo(() => {
    const { count, groups } = computeStrategyGroupCount(
      zeroDtePositions,
      positionStrategies,
    );

    logger.debug('[0DTE Count Debug]', {
      totalPositions: zeroDtePositions.length,
      groupedCount: count,
      strategyGroups: groups,
    });

    return count;
  }, [zeroDtePositions, positionStrategies]);

  // Merge real-time position updates from Account Streamer
  const mergePositionUpdate = useCallback((message: StreamerMessage | null) => {
    const normalized = normalizeStreamerPositionUpdate(message);
    if (!normalized) {
      return;
    }

    setPositions((prev) => {
      const next = [...prev];

      const index = next.findIndex(
        (position) =>
          position.symbol === normalized.symbol &&
          position["account-number"] === normalized.accountNumber,
      );

      const action = normalized.action?.toLowerCase() ?? "";
      const messageType = normalized.rawType.toLowerCase();

      const shouldRemove =
        action.includes("remove") ||
        action.includes("closed") ||
        action === "delete" ||
        messageType.includes("remove") ||
        messageType.includes("closed") ||
        normalized.quantity === 0;

      if (shouldRemove) {
        if (index >= 0) {
          logger.debug(
            `[UnifiedPositions] Removing position ${normalized.symbol} (quantity: ${normalized.quantity ?? "n/a"})`,
          );
          cacheAggregator.updatePosition(
            normalized.accountNumber,
            normalized.symbol,
            null,
          );
          next.splice(index, 1);
        }
        return next;
      }

      const mergedPosition: UnifiedPosition = {
        ...(index >= 0 ? next[index] : {}),
        ...normalized.payload,
        symbol: normalized.symbol,
        "account-number": normalized.accountNumber,
        lastUpdate: new Date(),
      } as UnifiedPosition;

      if (index >= 0) {
        next[index] = mergedPosition;
        logger.debug(
          `[UnifiedPositions] Updated position ${normalized.symbol} (quantity: ${mergedPosition.quantity})`,
        );
      } else {
        next.push(mergedPosition);
        logger.debug(
          `[UnifiedPositions] Added position ${normalized.symbol} (quantity: ${mergedPosition.quantity})`,
        );
      }

      cacheAggregator.updatePosition(
        normalized.accountNumber,
        normalized.symbol,
        mergedPosition,
      );

      return next;
    });

    setLastUpdate(new Date());
  },
    // We intentionally rely on functional state updates instead of the current
    // positions array, so we keep the dependency list empty.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Fetch positions from API
  const fetchPositions = useCallback(async () => {
    if (selectedAccounts.length === 0) {
      setPositions([]);
      setEnhancedPositions([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const allPositions: UnifiedPosition[] = [];

      const positionResults = await Promise.allSettled(
        selectedAccounts.map(async (account) => {
          const accountNumber = account.account["account-number"];
          const response = await apiClient.fetchPositions(accountNumber);
          const rawPositions = extractPositionsFromResponse(response);

          return rawPositions
            .filter((position) => {
              const quantityValue = Number.parseFloat(
                String(position.quantity ?? 0),
              );
              return Number.isFinite(quantityValue) ? quantityValue !== 0 : true;
            })
            .map((position) => {
              const extras = position as Position & PositionGreeksExtras;
              let streamerSymbol = extras["streamer-symbol"] ?? undefined;
              if (
                !streamerSymbol &&
                position["instrument-type"] === "Equity Option"
              ) {
                const mapped = symbolMappingService.getStreamerSymbol(
                  position.symbol,
                );
                if (typeof mapped === "string" && mapped.trim().length > 0) {
                  streamerSymbol = mapped;
                }
              }

              const unified: UnifiedPosition = {
                ...position,
                "account-number": accountNumber,
                "streamer-symbol": streamerSymbol,
                delta: extras.delta,
                theta: extras.theta,
                gamma: extras.gamma,
                vega: extras.vega,
                impliedVolatility: extras.impliedVolatility,
              };

              return unified;
            });
        }),
      );

      positionResults.forEach((result, index) => {
        const accountNumber =
          selectedAccounts[index]?.account["account-number"] ?? "unknown";
        if (result.status === "fulfilled") {
          allPositions.push(...result.value);
          return;
        }
        logger.error(`Failed to fetch positions for account ${accountNumber}:`, result.reason);
      });

      // Build strategy metadata map for filled strategies
      const accountNumbers = selectedAccounts.map(
        (account) => account.account["account-number"],
      );

      const strategyMap = new Map<string, PositionStrategyMetadata>();

      if (accountNumbers.length > 0) {
        console.log('[UnifiedPositions] Fetching strategy order metadata for accounts', accountNumbers);
        try {
          const response = await fetch(
            `/api/strategy-orders/positions?accountNumbers=${encodeURIComponent(
              accountNumbers.join(","),
            )}`,
            {
              credentials: "include" as RequestCredentials,
            },
          );

          console.log('[UnifiedPositions] Strategy metadata response status', response.status);

          if (response.ok) {
            const data = await response.json();
            if (Array.isArray(data.orders)) {
              const orders = data.orders as StrategyOrderSummary[];
              console.log('[UnifiedPositions] Loaded strategy orders', orders.length);
              orders.forEach((order) => {
                const metadata: PositionStrategyMetadata = {
                  orderId: order.orderId,
                  accountNumber: order.accountNumber,
                  strategyId: order.strategyId ?? null,
                  strategyName: order.strategyName ?? null,
                  strategyType: order.strategyType ?? null,
                  strategySource: order.strategySource ?? null,
                  symbol: order.symbol,
                  underlyingSymbol: order.underlyingSymbol ?? null,
                  status: order.status ?? null,
                  targetQuantity: order.targetQuantity ?? null,
                  profitTarget: order.profitTarget ?? null,
                  stopLoss: order.stopLoss ?? null,
                  trailingStop: order.trailingStop ?? null,
                  positionSymbols: Array.isArray(order.positionSymbols)
                    ? order.positionSymbols.filter(Boolean)
                    : [],
                };

                console.log('[UnifiedPositions] Strategy order metadata', {
                  orderId: metadata.orderId,
                  strategyId: metadata.strategyId,
                  strategyName: metadata.strategyName,
                  positionSymbols: metadata.positionSymbols,
                });

                metadata.positionSymbols.forEach((symbol: string) => {
                  const normalized = normalizePositionSymbol(symbol);
                  if (normalized) {
                    strategyMap.set(normalized, metadata);
                  }
                });
              });
              console.log(
                '[UnifiedPositions] Strategy map sample',
                Array.from(strategyMap.entries())
                  .slice(0, 5)
                  .map(([symbol, meta]) => ({
                    symbol,
                    strategyId: meta.strategyId,
                    strategyName: meta.strategyName,
                    legs: meta.positionSymbols.length,
                  })),
              );
            }
          } else if (response.status !== 404) {
            const errorText = await response.text();
            console.warn(
              "[UnifiedPositions] Failed to fetch strategy position metadata",
              response.status,
              errorText,
            );
          }
        } catch (metadataError) {
          console.warn(
            "[UnifiedPositions] Error loading strategy position metadata:",
            metadataError,
          );
        }
      }

      const enrichedPositions = allPositions.map((position) => {
        const normalizedSymbol = normalizePositionSymbol(position.symbol);
        const strategyMeta = strategyMap.get(normalizedSymbol);
        if (strategyMeta) {
          return {
            ...position,
            strategyMetadata: strategyMeta,
          };
        }
        return position;
      });

      // Only update if positions have actually changed
      let positionsUpdated = false;
      const nextSignature = JSON.stringify(
        summarizePositionsForComparison(enrichedPositions),
      );

      setPositions((prev) => {
        const previousSignature = JSON.stringify(
          summarizePositionsForComparison(prev),
        );

        const hasLengthChange = prev.length !== enrichedPositions.length;
        const hasContentChange = previousSignature !== nextSignature;

        if (hasLengthChange || hasContentChange) {
          positionsUpdated = true;
          return enrichedPositions;
        }

        return prev;
      });

      if (positionsUpdated) {
        setLastUpdate(new Date());
        logger.debug(
          `[UnifiedPositions] Positions updated: ${enrichedPositions.length} positions`,
        );
      } else {
        logger.debug(
          '[UnifiedPositions] No position changes detected, skipping state update',
        );
      }
      
      // Update strategy metadata map if changed
      setPositionStrategies((prev) => {
        if (prev.size === strategyMap.size) {
          let identical = true;
          for (const [symbol, value] of strategyMap.entries()) {
            const existing = prev.get(symbol);
            if (!existing || !strategyMetadataEquals(existing, value)) {
              identical = false;
              break;
            }
          }
          if (identical) {
            return prev;
          }
        }
        return strategyMap;
      });

      // Cache all positions
      enrichedPositions.forEach((position) => {
        const accountNumber = position["account-number"];
        if (!accountNumber) {
          return;
        }
        cacheAggregator.updatePosition(
          accountNumber,
          position.symbol,
          position,
        );
      });

      if (positionsUpdated) {
        try {
          const enhancedSnapshot = await positionMetricsService.enhancePositions(
            enrichedPositions,
          );
          setEnhancedPositions(enhancedSnapshot);

          positionEventBus.publish<PositionEvent>({
            type: "POSITION_UPDATED",
            symbol: "ALL",
            data: enhancedSnapshot,
            timestamp: new Date(),
            source: "api",
          });
        } catch (enhanceError) {
          logger.error(
            "[UnifiedPositions] Failed to enhance positions for event publication",
            enhanceError,
          );
        }
      }

      logger.debug(
        `[UnifiedPositions] Fetched and cached ${enrichedPositions.length} positions from ${selectedAccounts.length} accounts`,
      );

    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error';
      setError(errorMessage);
      logger.error("[UnifiedPositions] Error fetching positions:", err);
    } finally {
      setLoading(false);
    }
  }, [selectedAccounts]);

  // Listen to real-time position updates from SSE-backed account stream
  useEffect(() => {
    const timestampByAccount = positionEventTimestampsRef.current;

    Object.entries(accountPositionEvents).forEach(([, event]) => {
      const result = toStreamerMessageFromAccountEvent(event);
      if (!result) {
        return;
      }

      const previous = timestampByAccount[result.accountNumber] ?? 0;
      if (result.publishedAt <= previous) {
        return;
      }

      timestampByAccount[result.accountNumber] = result.publishedAt;
      lastStreamActivityRef.current = Math.max(
        lastStreamActivityRef.current,
        result.publishedAt,
      );

      logger.debug(
        "[UnifiedPositions] Account stream position update received",
        result,
      );
      mergePositionUpdate(result.message);
    });
  }, [accountPositionEvents, mergePositionUpdate]);

  // Determine market data subscriptions for positions and external requests
  useEffect(() => {
    const aliases = new Map<string, Set<string>>();
    const marketDataSymbolMap = new Map<string, MarketDataSymbol>();

    const register = (
      primarySymbol: string | null | undefined,
      extraAliases: Array<string | null | undefined> = [],
    ) => {
      if (!primarySymbol) {
        return;
      }

      const canonical = canonicalizeSymbol(primarySymbol);
      if (!canonical) {
        return;
      }

      let set = aliases.get(canonical);
      if (!set) {
        set = new Set<string>();
        aliases.set(canonical, set);
      }

      set.add(canonical);
      set.add(primarySymbol);

      extraAliases.forEach((alias) => {
        if (alias) {
          set!.add(alias);
        }
      });
    };

    const addMarketDataSymbol = (symbol: string | null | undefined, instrumentType: MarketDataSymbol["instrumentType"], streamerSymbol?: string | null, underlyingSymbol?: string | null) => {
      if (!symbol && !streamerSymbol) {
        return;
      }
      const key = (streamerSymbol || symbol || "").toString();
      if (!key) {
        return;
      }
      if (!marketDataSymbolMap.has(key)) {
        marketDataSymbolMap.set(key, {
          symbol: symbol || streamerSymbol || key,
          instrumentType,
          streamerSymbol: streamerSymbol ?? undefined,
          underlyingSymbol: underlyingSymbol ?? undefined,
        });
      }
    };

    positions.forEach((position) => {
      if (position["instrument-type"] === "Equity Option") {
        const positionSymbol = position.symbol?.toString() ?? "";
        const streamerSymbol =
          position["streamer-symbol"] ||
          symbolMappingService.getStreamerSymbol(positionSymbol);

        register(streamerSymbol || positionSymbol, [
          positionSymbol,
          streamerSymbol,
        ]);

        addMarketDataSymbol(positionSymbol, "option", streamerSymbol, position["underlying-symbol"]?.toString() ?? undefined);

        const underlying = position["underlying-symbol"]?.toString();
        if (underlying) {
          register(underlying, [underlying]);
          addMarketDataSymbol(underlying, "stock");
        }
      } else if (position["instrument-type"] === "Equity") {
        const equitySymbol = position.symbol?.toString();
        register(equitySymbol, [equitySymbol]);
        addMarketDataSymbol(equitySymbol, "stock");
      }
    });

    aggregatedExternalSymbols.forEach((value) => {
      const primary = value.streamerSymbol || value.symbol;
      register(primary, [
        value.symbol,
        value.streamerSymbol,
        value.underlyingSymbol,
      ]);

      addMarketDataSymbol(
        value.symbol,
        value.instrumentType,
        value.streamerSymbol,
        value.underlyingSymbol,
      );
    });

    setSubscriptionAliases((prev) =>
      aliasMapsEqual(prev, aliases) ? prev : cloneAliasMap(aliases),
    );

    if (marketDataSymbolMap.size > 0) {
      subscribeToMarketData(Array.from(marketDataSymbolMap.values()));
    } else {
      // Ensure stale subscriptions are cleared when no symbols remain
      subscribeToMarketData([]);
    }
  }, [
    positions,
    aggregatedExternalSymbols,
    subscribeToMarketData,
  ]);

  // Update market data map from streamed ticks
  useEffect(() => {
    const aliasesSnapshot = subscriptionAliases;
    const aliasKeys = new Set<string>();
    aliasesSnapshot.forEach((aliasSet) => {
      aliasSet.forEach((alias) => {
        if (alias) {
          aliasKeys.add(alias);
        }
      });
    });

    const nextMarketData = new Map<string, MarketQuote>();

    quotes.forEach((quote, symbol) => {
      nextMarketData.set(symbol, {
        bidPrice: quote.bidPrice ?? undefined,
        askPrice: quote.askPrice ?? undefined,
        lastPrice: quote.lastPrice ?? undefined,
        midPrice: quote.midPrice ?? undefined,
        volume: quote.volume ?? undefined,
        timestamp: quote.timestamp ?? undefined,
      });
    });

    for (const [canonical, tick] of Object.entries(ticks)) {
      const aliasSet = aliasesSnapshot.get(canonical) ?? new Set<string>([canonical]);

      const bid = typeof tick.bid === "number" && Number.isFinite(tick.bid) ? tick.bid : undefined;
      const ask = typeof tick.ask === "number" && Number.isFinite(tick.ask) ? tick.ask : undefined;
      const midFromTick =
        typeof tick.mid === "number" && Number.isFinite(tick.mid)
          ? tick.mid
          : bid != null && ask != null
            ? (bid + ask) / 2
            : undefined;
      const lastPrice =
        typeof tick.price === "number" && Number.isFinite(tick.price)
          ? tick.price
          : midFromTick;
      const timestamp = tick.timestamp ?? tick.publishedAt ?? Date.now();

      const overlay: MarketQuote = {
        bidPrice: bid,
        askPrice: ask,
        lastPrice,
        midPrice: midFromTick,
        timestamp,
      };

      aliasSet.forEach((alias) => {
        if (!alias) {
          return;
        }
        aliasKeys.add(alias);
        const existing = nextMarketData.get(alias);
        if (!existing) {
          nextMarketData.set(alias, overlay);
          return;
        }

        const merged: MarketQuote = {
          bidPrice: overlay.bidPrice ?? existing.bidPrice,
          askPrice: overlay.askPrice ?? existing.askPrice,
          lastPrice: overlay.lastPrice ?? existing.lastPrice,
          midPrice: overlay.midPrice ?? existing.midPrice,
          volume: existing.volume,
          timestamp: overlay.timestamp ?? existing.timestamp,
        };

        nextMarketData.set(alias, merged);
      });
    }

    // Remove entries that are no longer relevant (no quote and no alias)
    for (const key of Array.from(nextMarketData.keys())) {
      if (!quotes.has(key) && !aliasKeys.has(key)) {
        nextMarketData.delete(key);
      }
    }

    const previousSnapshot = marketDataRef.current;
    const changed =
      previousSnapshot.size !== nextMarketData.size ||
      Array.from(nextMarketData.entries()).some(([key, value]) => {
        const prev = previousSnapshot.get(key);
        return (
          !prev ||
          prev.lastPrice !== value.lastPrice ||
          prev.bidPrice !== value.bidPrice ||
          prev.askPrice !== value.askPrice ||
          prev.midPrice !== value.midPrice ||
          prev.timestamp !== value.timestamp
        );
      });

    if (changed) {
      marketDataRef.current = nextMarketData;
      marketDataListeners.current.forEach((listener) => listener());

      if (process.env.NODE_ENV !== "production") {
        console.debug(
          "[UnifiedPositions] marketData snapshot",
          Array.from(nextMarketData.keys()).slice(0, 20),
        );
      }

      positionEventBus.publish<MarketDataEvent>({
        type: "MARKET_DATA_UPDATED",
        symbol: "BULK",
        data: Object.fromEntries(nextMarketData),
        timestamp: new Date(),
        source: "api",
      });
    }
  }, [quotes, ticks, subscriptionAliases]);

  // Update Greeks data from streamed SSE payloads
  useEffect(() => {
    const aliasesSnapshot = subscriptionAliases;
    const aliasKeys = new Set<string>();
    aliasesSnapshot.forEach((aliasSet) => {
      aliasSet.forEach((alias) => {
        if (alias) {
          aliasKeys.add(alias);
        }
      });
    });

    const nextGreeks = new Map<string, Greeks>();

    greeks.forEach((value, symbol) => {
      nextGreeks.set(symbol, {
        delta: value.delta,
        gamma: value.gamma,
        theta: value.theta,
        vega: value.vega,
        impliedVolatility: value.impliedVolatility,
        timestamp: value.timestamp,
      });
    });

    for (const [canonical, greek] of Object.entries(streamedGreeks)) {
      const aliasSet = aliasesSnapshot.get(canonical) ?? new Set<string>([canonical]);
      const impliedVolatility =
        typeof greek.iv === "number" && Number.isFinite(greek.iv)
          ? greek.iv
          : undefined;

      const entry: Greeks = {
        delta: greek.delta,
        gamma: greek.gamma,
        theta: greek.theta,
        vega: greek.vega,
        impliedVolatility,
        timestamp: greek.timestamp ?? Date.now(),
      };

      aliasSet.forEach((alias) => {
        if (!alias) {
          return;
        }
        aliasKeys.add(alias);
        const existing = nextGreeks.get(alias);
        nextGreeks.set(alias, {
          delta: entry.delta ?? existing?.delta,
          gamma: entry.gamma ?? existing?.gamma,
          theta: entry.theta ?? existing?.theta,
          vega: entry.vega ?? existing?.vega,
          impliedVolatility: entry.impliedVolatility ?? existing?.impliedVolatility,
          timestamp: entry.timestamp ?? existing?.timestamp ?? Date.now(),
        });
      });
    }

    for (const key of Array.from(nextGreeks.keys())) {
      if (!greeks.has(key) && !aliasKeys.has(key)) {
        nextGreeks.delete(key);
      }
    }

    const previousSnapshot = greeksDataRef.current;
    const changed =
      previousSnapshot.size !== nextGreeks.size ||
      Array.from(nextGreeks.entries()).some(([key, value]) => {
        const prev = previousSnapshot.get(key);
        return (
          !prev ||
          prev.delta !== value.delta ||
          prev.gamma !== value.gamma ||
          prev.theta !== value.theta ||
          prev.vega !== value.vega ||
          prev.impliedVolatility !== value.impliedVolatility ||
          prev.timestamp !== value.timestamp
        );
      });

    if (changed) {
      greeksDataRef.current = nextGreeks;
      greeksDataListeners.current.forEach((listener) => listener());

      if (typeof window !== "undefined") {
        try {
          localStorage.setItem(
            "cachedGreeksData",
            JSON.stringify(Object.fromEntries(nextGreeks)),
          );
          localStorage.setItem(
            "cachedGreeksTimestamp",
            new Date().toISOString(),
          );
        } catch (e) {
          logger.error("Failed to cache Greeks:", e);
        }
      }

      positionEventBus.publish<GreeksEvent>({
        type: "GREEKS_UPDATED",
        symbol: "BULK",
        data: Object.fromEntries(nextGreeks),
        timestamp: new Date(),
        source: "api",
      });
    }
  }, [greeks, streamedGreeks, subscriptionAliases]);

  // Force cleanup of closed positions (quantity = 0)
  const cleanupClosedPositions = useCallback(() => {
    setPositions(prev => {
      const filtered = prev.filter((position) => {
        const quantityValue = Number.parseFloat(
          String(position.quantity ?? 0),
        );
        return Number.isFinite(quantityValue) ? quantityValue !== 0 : true;
      });
      const removedCount = prev.length - filtered.length;
      if (removedCount > 0) {
        logger.debug(`[UnifiedPositions] Cleaned up ${removedCount} closed positions`);
        setLastUpdate(new Date());
      }
      return filtered;
    });
  }, []);

  // The staleness interval below needs the latest lastUpdate, but depending on
  // lastUpdate directly re-ran this effect after the initial hydrate updated it
  // (measured: a guaranteed duplicate /api/positions/{account} fetch per page
  // load). Read it through a ref so hydrate runs once per account selection.
  const lastUpdateRef = useRef(lastUpdate);
  useEffect(() => {
    lastUpdateRef.current = lastUpdate;
  }, [lastUpdate]);

  // Hydrate on mount and refresh only when stream is stale
  useEffect(() => {
    if (accountNumbers.length === 0) {
      setPositions([]);
      setEnhancedPositions([]);
      return undefined;
    }

    let cancelled = false;
    let cleanupTimer: ReturnType<typeof setTimeout> | null = null;

    const performInitialFetch = async () => {
      await fetchPositions();
      if (!cancelled) {
        lastStreamActivityRef.current = Date.now();
        cleanupTimer = setTimeout(cleanupClosedPositions, 1000);
      }
    };

    void performInitialFetch();

    const FALLBACK_CHECK_MS = 15_000;
    const STALE_THRESHOLD_MS = 60_000;

    const interval = setInterval(() => {
      const lastEvent = Math.max(
        lastStreamActivityRef.current,
        lastUpdateRef.current?.getTime() ?? 0,
      );
      const staleDuration = Date.now() - lastEvent;

      if (staleDuration >= STALE_THRESHOLD_MS) {
        logger.debug("[UnifiedPositions] Stream idle, triggering fallback refresh", {
          accountCount: accountNumbers.length,
          staleDuration,
        });
        void fetchPositions().then(() => {
          if (!cancelled) {
            lastStreamActivityRef.current = Date.now();
            cleanupClosedPositions();
          }
        });
      }
    }, FALLBACK_CHECK_MS);

    return () => {
      cancelled = true;
      clearInterval(interval);
      if (cleanupTimer) {
        clearTimeout(cleanupTimer);
      }
    };
  }, [accountNumbers, cleanupClosedPositions, fetchPositions]);

  // Snapshot functions for external systems (monitoring)
  const getPositionsSnapshot = useCallback(() => {
    return [...positions]; // Return copy to prevent mutation
  }, [positions]);

  const getMarketDataSnapshot = useCallback(() => {
    return new Map(marketDataRef.current); // Return copy
  }, []);

  const refreshPositions = useCallback(async () => {
    await fetchPositions();
  }, [fetchPositions]);

  const marketDataSubscribe = useCallback((listener: () => void) => {
    marketDataListeners.current.add(listener);
    return () => {
      marketDataListeners.current.delete(listener);
    };
  }, []);

  const marketDataGetSnapshot = useCallback(() => marketDataRef.current, []);

  const greeksDataSubscribe = useCallback((listener: () => void) => {
    greeksDataListeners.current.add(listener);
    return () => {
      greeksDataListeners.current.delete(listener);
    };
  }, []);

  const greeksDataGetSnapshot = useCallback(() => greeksDataRef.current, []);

  const baseContextValue = useMemo<Omit<UnifiedPositionsContextType, "marketData" | "greeksData">>(() => ({
    positions,
    enhancedPositions,
    weeklyPositions,
    zeroDtePositions,
    weeklyGroupedCount,
    zeroDteGroupedCount,
    positionStrategies,
    isConnected: marketDataConnected,
    accountStreamerConnected,
    lastUpdate,
    refreshPositions,
    cleanupClosedPositions,
    loading,
    error,
    getPositionsSnapshot,
    getMarketDataSnapshot,
    addExternalSymbols,
    clearExternalSymbols,
  }), [
    positions,
    enhancedPositions,
    weeklyPositions,
    zeroDtePositions,
    weeklyGroupedCount,
    zeroDteGroupedCount,
    positionStrategies,
    marketDataConnected,
    accountStreamerConnected,
    lastUpdate,
    refreshPositions,
    cleanupClosedPositions,
    loading,
    error,
    getPositionsSnapshot,
    getMarketDataSnapshot,
    addExternalSymbols,
    clearExternalSymbols,
  ]);

  const contextValue = useMemo<UnifiedPositionsContextInternal>(() => ({
    base: baseContextValue,
    marketDataStore: {
      subscribe: marketDataSubscribe,
      getSnapshot: marketDataGetSnapshot,
    },
    greeksDataStore: {
      subscribe: greeksDataSubscribe,
      getSnapshot: greeksDataGetSnapshot,
    },
  }), [
    baseContextValue,
    marketDataSubscribe,
    marketDataGetSnapshot,
    greeksDataSubscribe,
    greeksDataGetSnapshot,
  ]);

  // Debug logging for context changes
  useEffect(() => {
    logger.debug('[UnifiedPositions] Context value updated', {
      positionsCount: positions.length,
      marketDataCount: marketDataRef.current.size,
      greeksCount: greeksDataRef.current.size,
      strategyMapCount: positionStrategies.size,
      marketDataConnected,
      accountStreamerConnected,
      loading,
      error,
      lastUpdate: lastUpdate?.toISOString()
    });
  }, [
    accountStreamerConnected,
    error,
    marketDataConnected,
    lastUpdate,
    loading,
    positionStrategies.size,
    positions.length,
  ]);

  return (
    <UnifiedPositionsContext.Provider value={contextValue}>
      {children}
    </UnifiedPositionsContext.Provider>
  );
}
